import { GP_VIEW_PERMISSION } from '@supply-mover-2-0/shared';
import type { ListBuilderConfiguration } from '../types';

/**
 * Payment status value mapping shared across invoice lists
 */
const PAYMENT_STATUS_VALUE_MAPPING = [
	{
		value: 'Outstanding',
		displayText: 'Outstanding',
		color: { type: 'warning' },
	},
	{
		value: 'Paid in Full',
		displayText: 'Paid in Full',
		color: { type: 'success' },
	},
	{
		value: 'Cancelled',
		displayText: 'Cancelled',
		color: { type: 'danger' },
	},
	// PD-16747 — the values `deriveInvoicePaymentStatus` returns besides the
	// three above. Without an entry the badge fell back to an unstyled raw
	// value, so a credit memo's state and a Closed invoice's read like noise.
	{
		value: 'Closed',
		displayText: 'Closed',
		color: { type: 'neutral' },
	},
	{
		value: 'Credit Outstanding',
		displayText: 'Credit Outstanding',
		color: { type: 'info' },
	},
	{
		value: 'Credit Applied',
		displayText: 'Credit Applied',
		color: { type: 'neutral' },
	},
];

/**
 * Invoice Order Embedded list configuration
 * Shows invoices for a specific order
 * Converted from JSON to TypeScript for type safety
 */
export const INVOICE_ORDER_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'order-embedded-invoices-all',
			cacheTTL: 600000,
			name: 'All Invoices',
			type: 'default',
			active: true,
			sortOrder: 0,
			columns: [
				{
					id: 'invoice-number',
					fields: [
						{
							id: 'invoice-external-id-field',
							link: {
								type: 'object',
								enabled: true,
								idField: '_id',
								objectTable: 'invoice',
							},
							dataConfig: {
								type: 'field',
								field: { fieldName: 'invoiceNumber', objectTable: 'invoice' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'statusInfo' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1-invoice', text: 'Invoice #' }],
					minWidth: 120,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'externalId',
							objectTable: 'invoice',
						},
					],
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'top',
				},
				{
					id: 'date',
					fields: [
						{
							id: 'date-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'date', objectTable: 'invoice' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								formatOptions: { dateFormat: 'short' },
							},
							displayStyle: 'date',
						},
					],
					headers: [{ id: 'h3', text: 'Date' }],
					minWidth: 80,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{ direction: 'DESC', fieldName: 'date', objectTable: 'invoice' },
					],
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'top',
				},
				{
					id: 'salesperson',
					fields: [
						{
							id: 'salesperson-name-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'salesRepFirstName',
									objectTable: 'invoice',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-salesperson', text: 'Sales Rep' }],
					minWidth: 100,
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'top',
				},
				{
					id: 'lines',
					fields: [
						{
							id: 'lines-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'numberOfLines', objectTable: 'invoice' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h4', text: 'Lines' }],
					minWidth: 60,
					sortable: true,
					alignment: 'center',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'numberOfLines',
							objectTable: 'invoice',
						},
					],
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'top',
				},
				{
					id: 'adjustments',
					fields: [
						{
							id: 'tax-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'tax', objectTable: 'invoice' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
							},
							displayStyle: 'currency',
						},
						{
							id: 'line-break-tax',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'freight-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'freight', objectTable: 'invoice' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'currency',
						},
						{
							id: 'line-break-freight',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'discount-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'discount', objectTable: 'invoice' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'currency',
						},
					],
					headers: [
						{ id: 'h-tax', text: 'Tax' },
						{ id: 'h-freight', text: 'Freight' },
						{ id: 'h-discount', text: 'Discount' },
					],
					minWidth: 90,
					sortable: true,
					alignment: 'right',
					sortFields: [
						{ direction: 'DESC', fieldName: 'tax', objectTable: 'invoice' },
					],
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'top',
				},
				{
					id: 'invoice-totals',
					fields: [
						{
							id: 'subtotal-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'subtotal', objectTable: 'invoice' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
							},
							displayStyle: 'currency',
						},
						{
							id: 'line-break-5',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'total-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'total', objectTable: 'invoice' },
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'currency',
						},
						{
							id: 'line-break-6',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'amount-owed-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'm_sm_payment_outstandingBalance',
									objectTable: 'invoice',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'currency',
						},
					],
					headers: [
						{ id: 'h6-subtotal', text: 'Subtotal' },
						{ id: 'h6-total', text: 'Total' },
						{ id: 'h6-amount-owed', text: 'Amount Owed' },
					],
					minWidth: 120,
					sortable: true,
					alignment: 'right',
					sortFields: [
						{ direction: 'DESC', fieldName: 'total', objectTable: 'invoice' },
					],
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'top',
				},
				{
					id: 'gp-totals',
					fields: [
						{
							id: 'total-gp-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'totalGP', objectTable: 'invoice' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
							},
							displayStyle: 'currency',
							requiredPermission: GP_VIEW_PERMISSION,
						},
					],
					headers: [{ id: 'h8-total-gp', text: 'GP' }],
					minWidth: 120,
					sortable: true,
					alignment: 'right',
					sortFields: [
						{ direction: 'DESC', fieldName: 'totalGP', objectTable: 'invoice' },
					],
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'top',
				},
				{
					id: 'payment-status',
					fields: [
						{
							id: 'payment-status-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'paymentStatus', objectTable: 'invoice' },
							},
							styleConfig: {
								size: 'xs',
								valueMapping: PAYMENT_STATUS_VALUE_MAPPING,
							},
							displayStyle: 'badge',
						},
					],
					headers: [{ id: 'h7', text: 'Payment Status' }],
					minWidth: 120,
					sortable: false,
					alignment: 'center',
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'center',
				},
			],
			description: 'Complete list of all invoices with key details',
			defaultSortId: 'column-date-desc',
			requiredFields: [
				'_id',
				'externalId',
				'externalId2',
				'invoiceNumber',
				'date',
				'_fk_client__id',
				'fk_company__id',
				'numberOfLines',
				'tax',
				'freight',
				'discount',
				'subtotal',
				'total',
				'salesRepFirstName',
				'companyName',
				'm_sm_payment_outstandingBalance',
				'paymentStatus',
				'totalGP',
			],
		},
	],
	paging: {
		defaultPageSize: 20,
		pageSizeOptions: [20, 50, 100],
	},
	showResetButton: true,
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/invoices/:id',
			objectLabel: 'Invoice',
			objectTable: 'invoice',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'externalId', fieldLabel: 'External ID' },
				{ fieldName: 'externalId2', fieldLabel: 'External ID 2' },
				{ fieldName: 'date', fieldLabel: 'Date' },
				{ fieldName: '_fk_client__id', fieldLabel: 'Client ID' },
				{ fieldName: 'fk_company__id', fieldLabel: 'Company ID' },
				{ fieldName: 'numberOfLines', fieldLabel: 'Number of Lines' },
				{ fieldName: 'tax', fieldLabel: 'Tax' },
				{ fieldName: 'freight', fieldLabel: 'Freight' },
				{ fieldName: 'discount', fieldLabel: 'Discount' },
				{ fieldName: 'subtotal', fieldLabel: 'Subtotal' },
				{ fieldName: 'total', fieldLabel: 'Total' },
				{ fieldName: 'salesRepFirstName', fieldLabel: 'Sales Rep First Name' },
			],
		},
	],
	tableSorts: [
		{
			id: 'date-desc',
			name: 'Date (Newest First)',
			fields: [
				{ direction: 'DESC', fieldName: 'date', objectTable: 'invoice' },
			],
			description: 'Sort by invoice date descending',
		},
		{
			id: 'date-asc',
			name: 'Date (Oldest First)',
			fields: [{ direction: 'ASC', fieldName: 'date', objectTable: 'invoice' }],
			description: 'Sort by invoice date ascending',
		},
	],
};
